import { ReactNode, useEffect, useRef } from 'react';
import { Descendant, Editor, Transforms } from 'slate';
import { Editable, Slate } from 'slate-react';
import Element from './Element';
import { useUploadEditor } from './withUpload';
import Toolbar from './Toolbar';
import { UploadProvider, useUploadValue } from './UploadContext';
import { EditableProps } from 'slate-react/dist/components/editable';
import { defaultDescendants, descendants2segments, getTextLength } from './utils';
import { noop } from 'lodash-es';
import styled from '@emotion/styled';
import styles from './style.module.css';
import { cx } from '@emotion/css';
import ErrorBoundary from 'components/ErrorBoundary';
import { Button } from 'antd';
import SegmentRender from './SegmentRender';

export interface UploadEditorProps extends Omit<EditableProps, 'value' | 'onChange'> {
  value?: Descendant[];
  onChange?: (value: Descendant[]) => void;
  maxLength?: number;
  headerRightExtraNode?: ReactNode;
  toolbarExtraNode?: ReactNode;
}

function UploadEditor(props: UploadEditorProps) {
  const {
    value = defaultDescendants,
    onChange = noop,
    maxLength,
    readOnly,
    className,
    style,
    headerRightExtraNode = null,
    toolbarExtraNode = null,
    ...otherProps
  } = props;

  const editor = useUploadEditor();

  const uploadValue = useUploadValue();

  const compositionRef = useRef(false);

  useEffect(() => {
    setTimeout(() => {
      Editor.normalize(editor, { force: true });
    }, 50);
  }, [editor]);

  const editable = (
    <Editable
      readOnly={readOnly}
      renderElement={(props) => <Element {...props} />}
      onCompositionStart={() => (compositionRef.current = true)}
      onCompositionEnd={() => (compositionRef.current = false)}
      scrollSelectionIntoView={() => {}}
      onBeforeInput={() => {
        setTimeout(() => {
          if (editor.selection) {
            editor.apply({
              type: 'set_selection',
              properties: null,
              newProperties: {
                anchor: editor.selection.anchor,
                focus: editor.selection.focus,
              },
            });
          }
        });
      }}
      onKeyDown={(event) => {
        if (compositionRef.current) {
          event.preventDefault();
          return;
        }

        if (event.key === 'Enter') {
          event.preventDefault();
          Transforms.insertText(editor, '\n');
        }
      }}
      {...otherProps}
    />
  );

  const withContent = (...content: ReactNode[]) => (
    <ErrorBoundary
      componentName="UploadEditor"
      fallback={(_, reset) => (
        <Button type="link" onClick={reset}>
          未知错误，点此恢复
        </Button>
      )}
    >
      <Slate editor={editor} value={value} onChange={onChange}>
        <UploadProvider value={uploadValue}>
          <Container className={cx(className, styles.editable)} style={style}>
            {content}
          </Container>
        </UploadProvider>
      </Slate>
    </ErrorBoundary>
  );

  if (readOnly) {
    return <SegmentRender segments={descendants2segments(value)} />;
  }

  return withContent(
    <header>
      <Toolbar onChange={onChange} extraNode={toolbarExtraNode} />
      <div className="ml-auto">{headerRightExtraNode}</div>
    </header>,
    editable,
    maxLength
      ? (() => {
          const textLength = getTextLength(value);
          return (
            <footer
              style={textLength > maxLength ? { color: 'var(--ant-error-color)' } : undefined}
            >
              {textLength}/{maxLength}
            </footer>
          );
        })()
      : null,
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 188px;
  border: solid 1px #f0f2f5;
  background: #fff;
  > header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    height: 39px;
    padding: 0 8px;
    border-bottom: solid 1px #f0f2f5;
    > * {
      margin-right: 4px;
    }
  }
  > footer {
    flex: 0 0 auto;
    padding: 0 18px 12px;
    text-align: right;
    color: #b5b9bf;
  }
  > div {
    flex: 1 0 0;
    padding: 12px;
    overflow: auto;
  }
`;

export default UploadEditor;
